import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  // Bell,
  // Calendar,
  Contact,
  // Eye,
  Flow,
  Folder,
  Home,
  // Inbox,
  // Outreach,
  Reports,
  Search,
  Settings,
  Templates,
  Circles,
  Interview,
  InternalJobsIcon,
  // Stack,
} from '@/assets/icons/nav-v2';
import { Atom01 } from '@kula-ai/icons-core';
import { NewAppNotification } from '@/components/notifications';
import { getReviews, reviewsApiActions } from '@/store/reviews';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { NavLink, TaskNav, NavSearch, NavProfile, Divider } from './components'; // add NavMenu component for secondary menu
import { useIsExternalUser } from '@/hooks/useIsExternalUser';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { UsersRight } from '@kula-ai/icons/outline/UsersRight';
import { usePermission } from '@/ui/permission';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { HelpSupport } from './help-and-support';
import { useKFlags } from '@/hooks/use-k-flags';
import { selectCurrUser } from '@/store/bootstrap/selectors';

export const useNavigationConfig = () => {
  const {
    feSettingsNav,
    feProjects,
    ats_enabled,
    feNoteTaker,
    feInterviewsRevamp,
    feReq,
    feAgentForgeV1,
    internalJobsPost,
  } = useKFlags();
  const { isCirclesEnabled, isFlowEnabled, isReportsEnabled } =
    usePermissionGuard();
  const dispatch = useDispatch();
  const {
    navigationReviews: {
      data: { data: { metrics_count: { awaiting_my_review } = {} } = {} } = {},
    },
  } = useSelector(getReviews);

  const isContactsEnabled = usePermission({
    permission: 'circles.contacts?',
  });
  const {
    isJobsVisible,
    isReportsEnabledForAtsUsers,
    requisitionsPermissions,
  } = useAtsPermissions();

  const isExternalUser = useIsExternalUser();

  // const isCommTemplatesEnabledForAts = usePermission({
  //   permission: 'api_ats_v1_ats_comm_template.create?',
  // });

  const currUser = useSelector(selectCurrUser);

  const isRequisitionsEnabled =
    feReq && currUser?.requisition?.enabled && requisitionsPermissions.index;

  const isJobsOrRequisitionsActive =
    window.location.pathname.startsWith('/jobs') ||
    window.location.pathname.startsWith('/requisitions');

  useEffect(() => {
    // Use separate reducer key for navigation data to avoid race condition with InternalReviewsList
    dispatch(reviewsApiActions('fetchNavigationReviews'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMemo(() => {
    const navItemsTopSection = [
      {
        component: NavSearch,
        props: { title: 'Search', icon: Search },
        styles: { my: '12px' },
        isEnabled: !ats_enabled,
      },
      {
        component: NewAppNotification,
        isEnabled: true,
        isAllowedToExternalUsers: true,
      },
      {
        component: TaskNav,
        isEnabled: isFlowEnabled || ats_enabled,
      },
      {
        component: Divider,
        props: {},
        styles: {
          borderBottom: '1px solid',
          borderBottomColor: 'secondary-100',
          my: '12px',
        },
        isEnabled: true,
        isAllowedToExternalUsers: true,
      },
    ];

    const navItemsMiddleSection = [
      {
        component: NavLink,
        props: {
          link: '/agent',
          title: 'AgentForge',
          icon: Atom01,
          testID: 'agents',
        },
        isEnabled: feAgentForgeV1, // all
      },
      {
        component: NavLink,
        props: {
          link: '/dashboard',
          title: 'Dashboard',
          icon: Home,
          testID: 'dashboard',
        },
        isEnabled: true, // all
      },
      {
        component: NavLink,
        props: {
          link: '/jobs',
          title: isRequisitionsEnabled ? 'Jobs & Requisitions' : 'Jobs',
          icon: LayersThree02,
          testID: 'jobs',
          // route is jobs or requisitions, so we need to show active state for both
          isCustomActive: isJobsOrRequisitionsActive,
        },
        // Todo-Req: Add permission check for requisitions later
        isEnabled: isJobsVisible && ats_enabled,
      },
      // {
      //   component: NavMenu,
      //   props: {
      //     // link: '/flows',
      //     title: 'Outreach',
      //     icon: Flow,
      //     menuItem: outReachItems,
      //   },
      //   isEnabled: isFlowEnabled,
      // },
      {
        component: NavLink,
        props: {
          link: '/flows',
          title: 'Flows',
          icon: Flow,
          testID: 'flows',
        },
        isEnabled: isFlowEnabled,
      },
      {
        component: NavLink,
        props: {
          link: '/projects',
          title: 'Projects',
          icon: Folder,
          count: awaiting_my_review,
          testID: 'projects',
        },
        isEnabled: (feProjects && isFlowEnabled) || isExternalUser,
        isAllowedToExternalUsers: true,
      },

      {
        component: NavLink,
        props: {
          link: '/circles',
          title: 'Circles',
          icon: Circles,
          testID: 'circles',
        },
        isEnabled: isCirclesEnabled,
      },
      {
        component: NavLink,
        props: {
          link: '/candidates',
          title: 'Candidates',
          icon: Contact,
          testID: 'contacts',
        },
        isEnabled: isFlowEnabled || isContactsEnabled,
      },
      {
        component: NavLink,
        props: {
          link: feInterviewsRevamp ? '/interviews/upcoming' : '/interviews/all',
          title: 'Interviews',
          icon: Interview,
          testID: 'interviews',
        },
        isEnabled: feNoteTaker || ats_enabled, // all can see
      },
      {
        component: NavLink,
        props: {
          link: '/templates',
          title: 'Templates',
          icon: Templates,
          testID: 'templates',
        },
        isEnabled: !ats_enabled && isFlowEnabled, // Only show when ats_enabled is false
      },
      {
        component: NavLink,
        props: {
          link: ats_enabled ? '/reports' : '/reports-home',
          title: 'Reports and Analytics',
          icon: Reports,
          testID: 'reports',
        },
        isEnabled:
          (isReportsEnabled && isFlowEnabled) || isReportsEnabledForAtsUsers,
      },
      {
        component: NavLink,
        props: {
          link: '/referrals',
          title: 'Referrals',
          icon: UsersRight,
          testID: 'referrals',
        },
        isEnabled: ats_enabled, // For all if ats enabled
      },
      {
        component: NavLink,
        props: {
          link: '/internal-job-board',
          title: 'Internal job board',
          icon: InternalJobsIcon,
          testID: 'internal-job-board',
        },
        isEnabled: internalJobsPost, // Gated behind internalJobsPost flag
      },
    ];

    const navBottomItems = [
      {
        component: Divider,
        styles: {
          borderBottom: '1px solid',
          borderBottomColor: 'secondary-100',
          my: '12px',
        },
        props: {},
        isEnabled: isFlowEnabled,
      },
      {
        component: HelpSupport,
        isEnabled: ats_enabled,
      },
      {
        component: NavLink,
        props: {
          link: ats_enabled
            ? '/settings'
            : feSettingsNav
              ? '/settings/profile/general'
              : '/settings/profile',
          title: 'Settings',
          icon: Settings,
          testID: 'settings',
        },
        isEnabled: true,
      },
      {
        component: NavProfile,
        isEnabled: !ats_enabled,
        isAllowedToExternalUsers: true,
      },
    ];

    return {
      navItemsTopSection,
      navItemsMiddleSection,
      navBottomItems,
    };
  }, [
    ats_enabled,
    isFlowEnabled,
    feAgentForgeV1,
    isRequisitionsEnabled,
    isJobsOrRequisitionsActive,
    isJobsVisible,
    feProjects,
    isExternalUser,
    awaiting_my_review,
    isCirclesEnabled,
    isContactsEnabled,
    feInterviewsRevamp,
    feNoteTaker,
    isReportsEnabled,
    isReportsEnabledForAtsUsers,
    internalJobsPost,
    feSettingsNav,
  ]);
};
